import { Link } from '@inertiajs/react'
import type { Pack } from '../lib/types'
import { useAudio } from '../contexts/AudioPlayer'
import PackArtwork from './PackArtwork'
import ComboArtwork from './ComboArtwork'

export default function PackCard({ pack }: { pack: Pack }) {
  const { play, isActive } = useAudio()
  const active = isActive(pack.id)
  const isCombo = pack.is_combo

  return (
    <div className="group border border-line hover:border-line-2 transition-colors">
      <div className="relative aspect-square overflow-hidden">
        <Link href={pack.url} className="block w-full h-full">
          {isCombo && !pack.has_image ? (
            <ComboArtwork members={pack.members} className="group-hover:scale-[1.02] transition-transform duration-300" />
          ) : (
            <PackArtwork pack={pack} size="full" className="group-hover:scale-[1.02] transition-transform duration-300" />
          )}
        </Link>

        {/* catalog number / bundle badge */}
        <span className="absolute top-2 left-2 mono-label !text-[10px] !text-paper/80 bg-ink/60 px-1.5 py-0.5 rounded">
          {isCombo ? 'BUNDLE' : pack.catalog_number}
        </span>

        {/* savings badge */}
        {isCombo && pack.savings && (
          <span className="absolute top-2 right-2 mono-label !text-[10px] !text-ink bg-emerald-400 px-1.5 py-0.5 rounded">
            Save {pack.savings}
          </span>
        )}

        {/* play preview (single packs only) */}
        {!isCombo && pack.audio_url && (
          <button
            type="button"
            onClick={() =>
              play({ id: pack.id, title: pack.name, subtitle: pack.manufacturer ?? 'Forma Sound', src: pack.audio_url! })
            }
            aria-label={active ? `Pause ${pack.name} preview` : `Play ${pack.name} preview`}
            className="absolute bottom-2 right-2 size-9 rounded-full bg-paper text-ink grid place-items-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition hover:scale-105"
          >
            {active ? (
              <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" /></svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9-5.5-9-5.5Z" /></svg>
            )}
          </button>
        )}
      </div>

      <div className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="mono-label">{pack.type_label}</span>
          <span className="flex items-center gap-2">
            {isCombo && pack.original_price && (
              <span className="mono-label tabular-nums !text-muted line-through">{pack.original_price}</span>
            )}
            <span className={`mono-label tabular-nums ${pack.free ? '!text-emerald-400' : '!text-paper'}`}>
              {pack.free ? 'Free' : pack.price}
            </span>
          </span>
        </div>
        <Link href={pack.url} className="block mt-1.5">
          <h3 className="text-base font-medium leading-snug group-hover:text-paper">{pack.name}</h3>
        </Link>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-xs text-muted">
            {isCombo ? `Includes ${pack.members.map((m) => m.name).join(' + ')}` : pack.manufacturer}
          </span>
          <Link href={pack.url} className="mono-label hover:text-paper transition-colors shrink-0 ml-2">View →</Link>
        </div>
      </div>
    </div>
  )
}
